import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../_theme/palette.js';
import { hexWithAlpha } from '../../../_theme/palette.js';
import { AnnouncementHtmlBody } from './AnnouncementHtmlBody.js';
import { TipTapRender } from '../TipTapRender.js';
import { formatPublishedDate, type AnnouncementShape } from '../types.js';

interface SingleAnnouncementBodyProps {
  palette: Palette;
  announcement: AnnouncementShape | null;
  compact?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  onDismiss: () => void;
  onCtaClick: () => void;
}

export function SingleAnnouncementBody({
  palette,
  announcement,
  compact,
  showBackButton,
  onBack,
  onDismiss,
  onCtaClick,
}: SingleAnnouncementBodyProps): JSX.Element {
  const publishedAt = formatPublishedDate(announcement?.publishedAt);
  const showCover = Boolean(announcement?.coverImage?.url);
  const collectionName = announcement?.collectionName;
  const hasCta = announcement?.cta?.enabled && announcement.cta.url;

  return (
    <>
      {/* When a cover image is present we drop the solid header band — the
          cover serves as the visual header instead. Close (and Back, in
          feed-detail mode) are overlaid in the top-right of the cover so
          the surface doesn't show two stacked "header" elements. */}
      {!showCover && (
        <header style={headerStyle(palette)}>
          <div style={headerLeftStyle()}>
            {showBackButton && onBack && (
              <IconButton palette={palette} onClick={onBack} ariaLabel="Back" glyph="‹" />
            )}
            {collectionName && <span style={collectionLabelStyle(palette)}>{collectionName}</span>}
          </div>
          <IconButton palette={palette} onClick={onDismiss} ariaLabel="Close" glyph="×" />
        </header>
      )}

      <div style={scrollStyle()}>
        {showCover ? (
          <div style={coverWrapStyle()}>
            <img
              src={announcement?.coverImage?.url}
              alt=""
              style={{ width: '100%', maxHeight: compact ? 220 : 320, objectFit: 'cover', display: 'block' }}
            />
            <div style={coverOverlayStyle()}>
              {showBackButton && onBack && (
                <IconButton palette={palette} onClick={onBack} ariaLabel="Back" glyph="‹" overlay />
              )}
              <IconButton palette={palette} onClick={onDismiss} ariaLabel="Close" glyph="×" overlay />
            </div>
            {collectionName && (
              <div style={coverCollectionLabelWrap()}>
                <span style={coverCollectionLabel(palette)}>{collectionName}</span>
              </div>
            )}
          </div>
        ) : (
          <div style={heroBandStyle(palette)} />
        )}

        <div style={contentStyle(compact)}>
          <div style={metaRowStyle(palette)}>
            {announcement?.badge && (
              <span style={badgeStyle(palette, announcement.badgeColor)}>{announcement.badge}</span>
            )}
            {publishedAt && <time style={publishedStyle(palette)}>{publishedAt}</time>}
          </div>
          <h1 style={titleStyle(palette, compact)}>
            {announcement?.title ?? 'Announcement'}
          </h1>
          {announcement?.description && (
            <p style={descriptionStyle(palette)}>{announcement.description}</p>
          )}
          {announcement?.bodyHtml ? (
            <div style={bodyStyle(palette)}>
              <AnnouncementHtmlBody palette={palette} html={announcement.bodyHtml} />
            </div>
          ) : Boolean(announcement?.body) && (
            <div style={bodyStyle(palette)}>
              <TipTapRender
                body={announcement?.body}
                options={{ color: palette.text, primary: palette.primary, radius: palette.radius }}
              />
            </div>
          )}

          {hasCta && (
            <div style={ctaRowStyle()}>
              <a
                href={announcement?.cta?.url}
                target="_top"
                rel="noopener noreferrer"
                onClick={onCtaClick}
                style={ctaStyle(palette)}
              >
                {announcement?.cta?.label ?? 'Learn more'}
                <span aria-hidden="true" style={{ marginLeft: 8 }}>→</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function IconButton({
  palette,
  onClick,
  ariaLabel,
  glyph,
  overlay,
}: {
  palette: Palette;
  onClick: () => void;
  ariaLabel: string;
  glyph: string;
  overlay?: boolean;
}): JSX.Element {
  // overlay variant sits on top of the cover image — uses a translucent
  // backdrop so it stays legible against any image. The default variant
  // lives inside the solid primary-color header band.
  const baseStyle = overlay ? iconButtonOverlayStyle() : iconButtonStyle(palette);
  const hoverBg = overlay ? 'rgba(0, 0, 0, 0.65)' : hexWithAlpha(palette.text, 0.06);
  const restBg = overlay ? 'rgba(0, 0, 0, 0.45)' : 'transparent';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={baseStyle}
      onMouseEnter={(event) => (event.currentTarget.style.background = hoverBg)}
      onMouseLeave={(event) => (event.currentTarget.style.background = restBg)}
    >
      {glyph}
    </button>
  );
}

function iconButtonOverlayStyle(): CSSProperties {
  return {
    width: 32,
    height: 32,
    borderRadius: 999,
    border: 'none',
    background: 'rgba(0, 0, 0, 0.45)',
    color: '#fff',
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 120ms ease',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  };
}

function coverOverlayStyle(): CSSProperties {
  return {
    position: 'absolute',
    top: 12,
    right: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    zIndex: 2,
  };
}

function coverCollectionLabelWrap(): CSSProperties {
  return {
    position: 'absolute',
    bottom: 12,
    left: 16,
    zIndex: 2,
  };
}

function coverCollectionLabel(palette: Palette): CSSProperties {
  return {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
    background: 'rgba(0, 0, 0, 0.55)',
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontFamily: palette.font,
    WebkitBackdropFilter: 'blur(4px)',
    backdropFilter: 'blur(4px)',
  };
}

function headerStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '14px 20px',
    background: palette.primary,
    color: palette.onPrimary,
    flexShrink: 0,
  };
}

function headerLeftStyle(): CSSProperties {
  return { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 };
}

function iconButtonStyle(palette: Palette): CSSProperties {
  return {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: palette.onPrimary,
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 120ms ease',
    opacity: 0.9,
  };
}

function collectionLabelStyle(palette: Palette): CSSProperties {
  return {
    fontSize: 11,
    fontWeight: 700,
    color: palette.onPrimary,
    textTransform: 'uppercase',
    letterSpacing: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    opacity: 0.95,
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function scrollStyle(): CSSProperties {
  return { flex: 1, overflowY: 'auto', minHeight: 0 };
}

function coverWrapStyle(): CSSProperties {
  return { width: '100%', background: 'rgba(15, 23, 42, 0.04)', position: 'relative' };
}

function heroBandStyle(palette: Palette): CSSProperties {
  return {
    height: 6,
    background: `linear-gradient(90deg, ${palette.primary}, ${hexWithAlpha(palette.primary, 0.4)})`,
  };
}

function contentStyle(compact?: boolean): CSSProperties {
  return {
    padding: compact ? '28px 28px 32px' : '36px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };
}

function metaRowStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
    color: palette.textMuted,
    fontSize: 13,
  };
}

function badgeStyle(palette: Palette, color: string | undefined): CSSProperties {
  const background = color ? hexWithAlpha(color, 0.14) : hexWithAlpha(palette.primary, 0.14);
  const foreground = color ?? palette.primary;
  return {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    background,
    color: foreground,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  };
}

function publishedStyle(palette: Palette): CSSProperties {
  return { fontSize: 13, color: palette.textMuted, fontWeight: 500 };
}

function titleStyle(palette: Palette, compact?: boolean): CSSProperties {
  return {
    margin: 0,
    fontSize: compact ? 22 : 28,
    fontWeight: 700,
    color: palette.text,
    lineHeight: 1.2,
    letterSpacing: -0.3,
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function descriptionStyle(palette: Palette): CSSProperties {
  return {
    margin: '12px 0 0',
    fontSize: 16,
    color: palette.textMuted,
    lineHeight: 1.6,
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function bodyStyle(palette: Palette): CSSProperties {
  return {
    marginTop: 24,
    color: palette.text,
    fontSize: 15,
    lineHeight: 1.7,
  };
}

function ctaRowStyle(): CSSProperties {
  return {
    marginTop: 28,
    display: 'flex',
    gap: 10,
  };
}

function ctaStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '12px 22px',
    background: palette.primary,
    color: palette.onPrimary,
    borderRadius: palette.radius,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
    boxShadow: `0 1px 2px rgba(15, 23, 42, 0.08), 0 6px 16px ${hexWithAlpha(palette.primary, 0.3)}`,
  };
}
